import { Link } from 'react-router-dom';
import Metadata from '../components/Metadata';

export default function OmOs() {
  const values = [
    {
      title: 'Erfaring & Ekspertise',
      description: 'Med mange års erfaring i branchen har vi opbygget en dybdegående viden om både traditionelle og moderne byggeteknikker.',
      icon: 'fa-regular fa-award',
    },
    {
      title: 'Kvalitetshåndværk',
      description: 'Vi går aldrig på kompromis med kvaliteten. Vi vælger de bedste materialer og udfører arbejdet med stor præcision.',
      icon: 'fa-regular fa-gem',
    },
    {
      title: 'Gensidig Tillid',
      description: 'Et ord er et ord. Vi overholder alle aftaler, tidsplaner og priser, så du kan føle dig helt tryg gennem hele forløbet.',
      icon: 'fa-regular fa-handshake',
    },
    {
      title: 'Personlig Service',
      description: 'Du samarbejder direkte med os. Vi lytter til dine ønsker og sikrer en tæt dialog fra start til slut.',
      icon: 'fa-regular fa-comments',
    },
  ];

  return (
    <>
      <Metadata
        title="Om os - K. Pheiffer Construction"
        description="Læs mere om K. Pheiffer Construction, vores erfaring og vores tilgang til kvalitetsbyggeri."
      />

      {/* Page Header */}
      <section className="relative text-white py-24 sm:py-32 overflow-hidden bg-slate-950">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-500 scale-105"
          style={{ backgroundImage: `url('https://assets.auxodev.dk/assets/cf131c5c-877c-4f32-8100-a69e4d795732/danish-craftsman-working.jpg')` }}
        />
        <div className="absolute inset-0 bg-black/75 backdrop-blur-[1px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-black/40" />

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-400">
            Hvem er vi?
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mt-2">
            Om K. Pheiffer Construction
          </h1>
          <p className="mt-4 text-slate-300 text-base sm:text-lg max-w-2xl mx-auto">
            Læs historien om vores virksomhed, vores passion for godt håndværk og vores kompromisløse tilgang til kvalitet.
          </p>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Content */}
            <div className="lg:col-span-7 space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
                Bygget på stolte håndværkstraditioner
              </h2>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                K. Pheiffer Construction blev grundlagt med en klar vision: At levere tømrerarbejde i absolut særklasse, hvor kunden altid er i centrum, og hvor ærlighed er fundamentet for ethvert samarbejde. Vi holder til på Snaremosevej i Fredericia, men løser opgaver for glade boligejere i hele Trekantområdet.
              </p>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                For os er tømrerfaget ikke bare et arbejde – det er et håndværk, vi er stolte af. Vi sætter en ære i de små detaljer, de præcise samlinger og den flotte finish, der gør, at dit projekt skiller sig ud og holder i mange år fremover.
              </p>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                Uanset om vi skal udskifte et enkelt vindue, bygge en ny træterrasse eller stå for en totalrenovering af dit hjem, møder vi altid op med et smil, overholder vores aftaler og rydder pænt op efter os selv. Det kalder vi godt, jysk håndværkerhjerte.
              </p>
            </div>

            {/* Video */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 rounded-2xl bg-slate-50 -z-10" />
                <video
                  src="https://assets.auxodev.dk/assets/cf131c5c-877c-4f32-8100-a69e4d795732/hero-video.mp4"
                  poster="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/cf131c5c-877c-4f32-8100-a69e4d795732/tagbra-dder-fa-r-paptag.jpg"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="rounded-xl shadow-md w-full object-cover aspect-[4/3] sm:aspect-[3/2] lg:aspect-square pointer-events-none select-none"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 sm:py-24 bg-slate-50 border-y border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">
              Vores værdier
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
              Hvad du kan forvente af os
            </h2>
            <p className="mt-4 text-slate-500 text-sm sm:text-base">
              Vi arbejder ud fra fire grundlæggende principper, der sikrer dig den bedste oplevelse og det bedste resultat.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((val, idx) => (
              <div
                key={idx}
                className="bg-white p-6 sm:p-8 rounded-xl border border-slate-100 shadow-sm flex gap-5 items-start"
              >
                <div className="h-12 w-12 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0 border border-emerald-100">
                  <i className={`${val.icon} text-xl`} />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-bold text-slate-900">{val.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{val.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
            Lad os tage en snak om dit projekt
          </h2>
          <p className="mt-4 text-slate-500 text-sm sm:text-base max-w-xl mx-auto">
            Vi rådgiver dig gerne om materialer, design og løsninger, så du får det helt rigtige resultat.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center rounded bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3 text-sm font-semibold tracking-wide transition-colors"
            >
              Kontakt os i dag
            </Link>
            <Link
              to="/jeg-tilbyder"
              className="inline-flex items-center justify-center rounded border border-slate-200 hover:border-slate-900 text-slate-700 hover:text-slate-900 px-6 py-3 text-sm font-semibold tracking-wide transition-colors"
            >
              Se vores ydelser
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
